import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getColorLevels } from './utils/colorSystem';
import { TreeCanvas } from './components/TreeCanvas';
import { AddPersonModal, shrinkPhoto } from './components/AddPersonModal';
import { PhotoEditor } from './components/PhotoEditor';
import { CardPreview } from './components/CardPreview';
import { PersonDetailPanel } from './components/PersonDetailPanel';
import { useTreeData } from './hooks/useTreeData';
import { LimitModal } from './components/LimitModal';
import { simulateAdd, simulateEdit, checkChange } from './utils/treeRules';
import { PriceModal } from './components/PriceModal';
import { OrderModal } from './components/OrderModal';
import { WelcomeScreen } from './components/WelcomeScreen';
import { UserDashboard } from './components/UserDashboard';
import { Tutorial, TutorialOffer } from './components/Tutorial';
import { InviteScreen } from './components/InviteScreen';
import './App.css';

const API = 'https://editor.bandeleart.ch';
const AUTOSAVE_INTERVAL = 30000; // 30 Sekunden

// ── Abgleich mit dem Server (mehrere Geräte) ───────────────────────────
// UNSAVED_KEY: Code des Stammbaums mit lokalen, noch NICHT gespeicherten Änderungen.
// FRESH_KEY:   Stammbaum wurde soeben vom Server geladen (vor dem Seiten-Reload).
// Sonst wird beim Öffnen IMMER der aktuelle Stand vom Server geholt – damit
// z.B. ein auf dem Handy eingefügtes Foto auch auf dem PC erscheint und ein
// alter Stand im Browser nie den neueren auf dem Server überschreibt.
const UNSAVED_KEY = 'familytree_unsaved';
const TUTORIAL_OFFER_KEY = 'familytree_tutorial_offer'; // neuer Stammbaum → Tutorial anbieten
const FRESH_KEY = 'familytree_fresh';
const HELPER_TUT_KEY = 'familytree_helper_tutorial_seen'; // Stammbäume, bei denen der Helfer die Einführung schon gesehen hat
function helperTutSeen() {
  try { return JSON.parse(localStorage.getItem(HELPER_TUT_KEY) || '[]'); } catch { return []; }
}

// ── Automatische Abmeldung ────────────────────────────────────────────
// Nach 7 Tagen seit dem letzten Login (Code per E-Mail) muss man sich neu einloggen.
const LOGIN_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const LOGIN_AT_KEY = 'stammbaum_login_at';
const SESSION_KEYS = ['stammbaum_session', 'stammbaum_user_session', 'familytree_data', LOGIN_AT_KEY];

function markLogin() {
  try { localStorage.setItem(LOGIN_AT_KEY, String(Date.now())); } catch {}
}

function isLoginExpired() {
  try {
    const hasSession = localStorage.getItem('stammbaum_session') || localStorage.getItem('stammbaum_user_session');
    if (!hasSession) return false;
    const at = parseInt(localStorage.getItem(LOGIN_AT_KEY) || '0', 10);
    return !at || Date.now() - at > LOGIN_MAX_AGE;
  } catch { return false; }
}

function clearLoginData() {
  SESSION_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch {} });
}

// ── Login-Token & Tab-Kennung ──────────────────────────────────────────
export function authHeader() {
  try {
    const u = JSON.parse(localStorage.getItem('stammbaum_user_session') || 'null');
    return u?.token ? { Authorization: `Bearer ${u.token}` } : {};
  } catch { return {}; }
}
function authToken() {
  try { return JSON.parse(localStorage.getItem('stammbaum_user_session') || 'null')?.token || ''; } catch { return ''; }
}
// Zufällige Kennung pro Browser-Tab (für die Bearbeitungs-Sperre)
const CLIENT_ID = (() => {
  try {
    let id = sessionStorage.getItem('client_id');
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('client_id', id); }
    return id;
  } catch { return Math.random().toString(36).slice(2); }
})();
const LOCK_HEARTBEAT = 8000; // Sperre alle 8 s erneuern (läuft serverseitig nach 2 Min. ab); so merkt man schnell, wenn jemand übernehmen möchte

// ── Stammbaum mit Fortschrittsanzeige vom Server laden ────────────────
// onProgress(anteil 0..1) – oder null, wenn die Grösse unbekannt ist.
async function loadTreeWithProgress(treeCode, onProgress) {
  const res = await fetch(`${API}/api/tree/load`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ treeCode }),
  });
  if (!res.ok) {
    const err = new Error('load failed');
    err.status = res.status;
    throw err;
  }
  const total = parseInt(res.headers.get('Content-Length') || '0', 10);
  if (!res.body || !res.body.getReader || !total) {
    onProgress(null);
    return res.json();
  }
  const reader = res.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(Math.min(1, received / total));
  }
  const bytes = new Uint8Array(received);
  let pos = 0;
  for (const c of chunks) { bytes.set(c, pos); pos += c.length; }
  return JSON.parse(new TextDecoder('utf-8').decode(bytes));
}

// POST mit Upload-Fortschritt (fetch kann den Upload-Fortschritt nicht melden)
function postJsonWithProgress(url, body, onProgress) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/json');
    const t = authToken();
    if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    }
    xhr.onload = () => {
      let json = null;
      try { json = JSON.parse(xhr.responseText); } catch {}
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0 });
    xhr.ontimeout = () => resolve({ ok: false, status: 0 });
    xhr.send(body);
  });
}

function TreeLoadingOverlay({ progress, label = 'Stammbaum wird geladen…' }) {
  const pct = progress == null ? null : Math.round(progress * 100);
  return (
    <div className="tree-loading-overlay">
      <div className="tree-loading-card">
        <div>{label}</div>
        <div className="tree-loading-track">
          {pct == null
            ? <div className="tree-loading-fill tree-loading-fill--indeterminate" />
            : <div className="tree-loading-fill" style={{ width: `${pct}%` }} />}
        </div>
        <div className="tree-loading-percent">
          {pct == null ? 'Bei vielen Fotos kann das einen Moment dauern.' : `${pct} %`}
        </div>
      </div>
    </div>
  );
}

// Beim Laden der Seite: abgelaufene Logins sofort entfernen (bevor etwas angezeigt wird).
// Bestehende Logins ohne Zeitstempel (vor diesem Update) gelten ebenfalls als abgelaufen.
if (isLoginExpired()) clearLoginData();

// „Direkt einloggen“-Link aus der Code-Mail (…/?login=EMAIL&code=123456):
// Daten merken, aus der Adresszeile entfernen und ein evtl. anderes Login abmelden.
let MAGIC_LOGIN = null;
(function captureMagicLogin() {
  try {
    const url = new URL(window.location.href);
    const email = (url.searchParams.get('login') || '').trim().toLowerCase();
    const code = (url.searchParams.get('code') || '').replace(/\D/g, '');
    if (!email && !code) return;
    url.searchParams.delete('login');
    url.searchParams.delete('code');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    if (!email.includes('@') || code.length !== 6) return;
    const current = JSON.parse(localStorage.getItem('stammbaum_user_session') || 'null');
    if (current?.email && current.email.toLowerCase() === email && !isLoginExpired()) return; // schon eingeloggt
    clearLoginData(); // anderes / abgelaufenes Login entfernen → Login-Seite erscheint
    MAGIC_LOGIN = { email, code };
  } catch {}
})();

// Einladungs-Link (…/?invite=TOKEN): Token merken, aus der Adresszeile entfernen
let INVITE_TOKEN = null;
(function captureInvite() {
  try {
    const url = new URL(window.location.href);
    const t = url.searchParams.get('invite');
    if (!t) return;
    url.searchParams.delete('invite');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    INVITE_TOKEN = t.replace(/[^a-f0-9]/gi, '');
  } catch {}
})();

// Nach „Einladung annehmen“: Login-Feld mit der eingeladenen Adresse vorbelegen
let PREFILL_LOGIN = null;
try { PREFILL_LOGIN = sessionStorage.getItem('prefill_login'); sessionStorage.removeItem('prefill_login'); } catch {}

// Link aus einer Mail (…/?tree=CODE): Code merken und aus der Adresszeile entfernen.
// Geöffnet wird der Stammbaum, sobald der Benutzer eingeloggt ist (siehe App).
(function captureTreeLink() {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('tree');
    if (!code) return;
    sessionStorage.setItem('open_tree', code.trim().toUpperCase());
    url.searchParams.delete('tree');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {}
})();

export default function App() {
  const { persons, relationships, addPerson, updatePerson, deletePerson, importData, clearAll, customColors, setCustomColor } = useTreeData();
  const [showAddModal, setShowAddModal]   = useState(false);
  const [editPersonId, setEditPersonId]   = useState(null);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [presetRelation, setPresetRelation] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(null); // null | 'generations' | 'topGeneration'
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isOrdered, setIsOrdered] = useState(() => {
    try {
      const savedSession = JSON.parse(localStorage.getItem('stammbaum_session') || 'null');
      if (savedSession?.readOnly) return true;
      const savedData = JSON.parse(localStorage.getItem('familytree_data') || 'null');
      return savedData?.status === 'in_bearbeitung' || savedData?.status === 'fertiggestellt';
    } catch { return false; }
  });
  const [session, setSession] = useState(() => {
    try {
      const saved = localStorage.getItem('stammbaum_session');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [userSession, setUserSession] = useState(() => {
    try {
      const saved = localStorage.getItem('stammbaum_user_session');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showNewTreeFromDashboard, setShowNewTreeFromDashboard] = useState(false);
  // Ladebalken: aktiv, wenn ein bestehender Stammbaum geöffnet ist, seine Daten aber noch vom Server kommen
  const [treeLoading, setTreeLoading] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('stammbaum_session') || 'null');
      const d = JSON.parse(localStorage.getItem('familytree_data') || 'null');
      if (!s?.treeCode) return false;
      if (sessionStorage.getItem(FRESH_KEY) === s.treeCode) return false;
      if (localStorage.getItem(UNSAVED_KEY) === s.treeCode && d?.persons) return false;
      return true;
    } catch { return false; }
  });
  const [loadProgress, setLoadProgress] = useState(null);
  const [leaving, setLeaving] = useState(false);         // „← Meine Stammbäume“: wird gespeichert
  const [leaveProgress, setLeaveProgress] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const lastSavedRef = useRef(null);
  const frozenRef = useRef(false); // true, wenn in einem anderen Tab ein anderer Stammbaum geöffnet wurde
  const [otherTabNotice, setOtherTabNotice] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  // Bearbeitungs-Sperre: { email, sameUser } wenn jemand anderes gerade bearbeitet
  const [lockedBy, setLockedBy] = useState(null);
  const lockedByRef = useRef(null);
  useEffect(() => { lockedByRef.current = lockedBy; }, [lockedBy]);
  const hasLockRef = useRef(false);
  // Einladung (…/?invite=TOKEN)
  const [invite, setInvite] = useState(() => (INVITE_TOKEN ? { token: INVITE_TOKEN, info: null, error: null } : null));
  const [showTutorialOffer, setShowTutorialOffer] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('stammbaum_session') || 'null');
      return !!s?.treeCode && localStorage.getItem(TUTORIAL_OFFER_KEY) === s.treeCode;
    } catch { return false; }
  });
  const closeTutorialOffer = (start) => {
    try { localStorage.removeItem(TUTORIAL_OFFER_KEY); } catch {}
    try {
      const code = JSON.parse(localStorage.getItem('stammbaum_session') || 'null')?.treeCode;
      const seen = helperTutSeen();
      if (code && !seen.includes(code)) localStorage.setItem(HELPER_TUT_KEY, JSON.stringify([...seen, code].slice(-50)));
    } catch {}
    setShowTutorialOffer(false);
    if (start) setShowTutorial(true);
  }; // „⋯“-Menü im Header (nur auf schmalen Bildschirmen)
  const isDirtyRef = useRef(false);
  const changeCountRef = useRef(0);    // zählt Änderungen (damit beim Speichern keine verloren gehen)
  const skipDirtyRef = useRef(true);   // nächste Datenänderung ist ein Laden, keine Bearbeitung
  const handleSaveRef = useRef(null);  // aktuelle Speicher-Funktion (für Timer)
  const saveTimerRef = useRef(null);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const [showPhotoWarning, setShowPhotoWarning] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const nameInputRef = useRef(null);

  // Markiere als geändert wenn Personen/Beziehungen sich ändern
  // (nicht beim ersten Anzeigen und nicht beim Laden vom Server)
  const lastSeenRef = useRef(null);
  useEffect(() => {
    const prev = lastSeenRef.current;
    lastSeenRef.current = { persons, relationships, customColors };
    if (skipDirtyRef.current) { skipDirtyRef.current = false; return; }
    // gleiche Daten wie zuvor (z.B. doppelter Effekt-Aufruf) → keine Änderung
    if (prev && prev.persons === persons && prev.relationships === relationships && prev.customColors === customColors) return;
    if (session?.treeCode) {
      isDirtyRef.current = true;
      changeCountRef.current += 1;
      try { localStorage.setItem(UNSAVED_KEY, session.treeCode); } catch {}
      // 2 Sekunden nach der letzten Änderung speichern (nicht erst nach bis zu 30 s)
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => handleSaveRef.current && handleSaveRef.current(), 2000);
    }
  }, [persons, relationships, customColors]);

  // Nach erfolgreichem Speichern: „ungespeichert“ nur zurücksetzen, wenn seither nichts geändert wurde
  const markSaved = (countAtStart, treeCode) => {
    if (changeCountRef.current !== countAtStart) return;
    isDirtyRef.current = false;
    try { if (localStorage.getItem(UNSAVED_KEY) === treeCode) localStorage.removeItem(UNSAVED_KEY); } catch {}
  };

  // Auto-Save alle 30 Sekunden
  useEffect(() => {
    if (!session?.treeCode) return;

    // Sicherheitsnetz: alle 30 s erneut versuchen, falls noch etwas ungespeichert ist
    const retry = () => { if (isDirtyRef.current && handleSaveRef.current) handleSaveRef.current(); };
    const interval = setInterval(retry, AUTOSAVE_INTERVAL);
    // Handy: Beim Zurückkehren zur Seite (App war im Hintergrund) sofort nachholen
    const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [session]);

  // Gibt true (gespeichert / nichts zu tun), false (Fehler) oder null (läuft bereits) zurück
  const handleSave = useCallback(async (onProgress) => {
    if (!session?.treeCode) return true;
    if (session.readOnly) return true; // nur angeschaut → nie überschreiben
    if (frozenRef.current) return true;
    if (lockedByRef.current) return true; // jemand anderes bearbeitet → nicht speichern
    // Nicht speichern wenn bestehender Stammbaum noch leer ist
    if (!session.isNew && persons.length === 0) return true;
    if (savingRef.current) { pendingSaveRef.current = true; return null; } // läuft schon → danach nochmals
    savingRef.current = true;
    const countAtStart = changeCountRef.current;
    setSaveStatus('saving');
    let success = false;
    try {
      const res = await postJsonWithProgress(
        `${API}/api/tree/save`,
        JSON.stringify({ treeCode: session.treeCode, data: getDataForSave(), clientId: CLIENT_ID }),
        typeof onProgress === 'function' ? onProgress : null
      );
      if (res.ok) {
        success = true;
        markSaved(countAtStart, session.treeCode);
        lastSavedRef.current = new Date();
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } else if (res.status === 423) {
        // Sperre verloren: jemand anderes bearbeitet jetzt
        hasLockRef.current = false;
        setLockedBy({ email: res.json?.lockedBy || 'jemand anderes', sameUser: false, lost: true });
        setSaveStatus('error');
      } else if (res.status === 401) {
        setSaveStatus('error');
        alert('Dein Login ist abgelaufen. Bitte melde dich neu an.');
        clearLoginData();
        window.location.reload();
      } else {
        setSaveStatus(res.status === 413 ? 'toolarge' : 'error');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      savingRef.current = false;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        setTimeout(() => handleSaveRef.current && handleSaveRef.current(), 0);
      }
    }
    return success;
  }, [session, persons, relationships, customColors, isOrdered]); // eslint-disable-line
  handleSaveRef.current = handleSave;

  const getDataForSave = useCallback(() => {
    return {
      persons,
      relationships,
      customColors,
      status: isOrdered ? 'in_bearbeitung' : 'erstellt',
    };
  }, [persons, relationships, customColors, isOrdered]);
  const handleLogin = useCallback((loginData) => {
    // loginData: { email, token, trees }
    const us = { email: loginData.email, token: loginData.token };
    try { localStorage.setItem('stammbaum_user_session', JSON.stringify(us)); } catch {}
    markLogin();
    setUserSession(us);
    setShowNewTreeFromDashboard(false);
  }, []);

  // Während die Seite offen ist: jede Minute und beim Zurückkehren zum Tab prüfen
  useEffect(() => {
    let done = false;
    const check = async () => {
      if (done || !isLoginExpired()) return;
      done = true;
      try { await handleSave(); } catch {}
      clearLoginData();
      window.location.reload();
    };
    const interval = setInterval(check, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [handleSave]);

  const handleUserLogout = useCallback(() => {
    localStorage.removeItem('stammbaum_user_session');
    localStorage.removeItem('stammbaum_session');
    setUserSession(null);
    setSession(null);
    clearAll();
  }, [clearAll]);

  const handleSelectTree = useCallback((tree, readOnly) => {
    // Load selected tree from dashboard
    const sessionMeta = {
      isNew: false,
      treeCode: tree.code,
      treeName: tree.name,
      email: userSession?.email || '',
      readOnly: readOnly || false,
      role: tree.role || 'owner',
    };
    try { localStorage.setItem('stammbaum_session', JSON.stringify(sessionMeta)); } catch {}
    // Load tree data then reload
    setLoadProgress(null);
    setTreeLoading(true);
    loadTreeWithProgress(tree.code, setLoadProgress)
      .then(serverData => {
        const treeData = (serverData.data && serverData.data.persons)
          ? serverData.data
          : serverData;
        try {
          localStorage.setItem('familytree_data', JSON.stringify(treeData));
          localStorage.removeItem(UNSAVED_KEY);
          sessionStorage.setItem(FRESH_KEY, tree.code); // nach dem Reload nicht nochmals laden
        } catch {}
        window.location.reload();
      })
      .catch(() => { window.location.reload(); });
  }, [userSession]);

  // ── Stammbaum aus Mail-Link öffnen (…/?tree=CODE) ─────────────────────
  useEffect(() => {
    let pending = null;
    try { pending = sessionStorage.getItem('open_tree'); } catch {}
    if (!pending) return;
    if (session?.treeCode === pending) { try { sessionStorage.removeItem('open_tree'); } catch {} return; }
    if (!userSession?.token) {
      // Nicht eingeloggt: zuerst Login zeigen, danach wird der Stammbaum geöffnet
      if (session) {
        localStorage.removeItem('stammbaum_session');
        localStorage.removeItem('familytree_data');
        setSession(null);
        setTreeLoading(false);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/user/trees`, { headers: { Authorization: `Bearer ${userSession.token}` } });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        try { sessionStorage.removeItem('open_tree'); } catch {}
        const t = (d.trees || []).find(x => x.code === pending);
        if (t) handleSelectTree(t, t.status !== 'erstellt'); // nur eigene Stammbäume
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [userSession, session, handleSelectTree]);

  // ── Anderer Tab hat einen anderen Stammbaum geöffnet → diesen Tab sperren ─
  // (Alle Tabs teilen sich den Browser-Speicher; so werden keine Daten vermischt.)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'stammbaum_session' || !session?.treeCode || frozenRef.current) return;
      let other = null;
      try { other = JSON.parse(e.newValue || 'null'); } catch {}
      if (other?.treeCode === session.treeCode) return;
      handleSave().catch(() => {});   // eigenen Stand noch sichern
      frozenRef.current = true;
      setOtherTabNotice(true);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [session, handleSave]);

  const handleBackToDashboard = useCallback(async () => {
    if (session?.treeCode) {
      setLeaveProgress(null);
      setLeaving(true);
      // laufende Speicherung abwarten, dann ggf. letzte Änderungen speichern
      clearTimeout(saveTimerRef.current);
      for (let i = 0; i < 600 && savingRef.current; i++) await new Promise(r => setTimeout(r, 100));
      let unsaved = isDirtyRef.current;
      try { unsaved = unsaved || localStorage.getItem(UNSAVED_KEY) === session.treeCode; } catch {}
      if (unsaved && !session.readOnly) {
        const ok = await handleSaveRef.current(p => setLeaveProgress(p));
        if (ok === false && !window.confirm(
          'Die letzten Änderungen konnten nicht gespeichert werden (Internetverbindung?).\n\n' +
          'Trotzdem zur Übersicht? Die ungespeicherten Änderungen gehen dann verloren.'
        )) {
          setLeaving(false);
          return;
        }
      }
    }
    try { localStorage.removeItem(UNSAVED_KEY); } catch {}
    if (session?.treeCode && hasLockRef.current) {
      hasLockRef.current = false;
      fetch(`${API}/api/user/tree/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ treeCode: session.treeCode, clientId: CLIENT_ID }),
      }).catch(() => {});
    }
    setLockedBy(null);
    localStorage.removeItem('stammbaum_session');
    localStorage.removeItem('familytree_data');
    setSession(null);
    clearAll();
    setLeaving(false);
  }, [session, handleSave, clearAll]);

  const handleStart = useCallback((s) => {
    // Nur Metadaten in der Session speichern (KEIN data-Feld mit Fotos → kein QuotaExceeded)
    const sessionMeta = {
      isNew:    s.isNew,
      treeCode: s.treeCode,
      treeName: s.treeName,
      email:    s.email,
    };
    try {
      localStorage.setItem('stammbaum_session', JSON.stringify(sessionMeta));
    } catch { /* sollte nie passieren, nur Metadaten */ }
    if (!s.keepLoginTime) markLogin(); // E-Mail wurde soeben per Code bestätigt

    try { localStorage.removeItem(UNSAVED_KEY); } catch {}
    if (!s.isNew && s.data) {
      // Baum-Daten separat speichern (Best Effort — bei grossen Fotos kann das scheitern)
      try {
        localStorage.setItem('familytree_data', JSON.stringify(s.data));
        sessionStorage.setItem(FRESH_KEY, s.treeCode);
      } catch {
        // QuotaExceeded: Fotos zu gross. Der Reload-Effekt lädt Daten dann frisch vom Server.
      }
    } else {
      // Neuer Stammbaum: alte Daten löschen
      localStorage.removeItem('familytree_data');
      try { sessionStorage.setItem(FRESH_KEY, s.treeCode); } catch {}
      if (s.isNew) { try { localStorage.setItem(TUTORIAL_OFFER_KEY, s.treeCode); } catch {} }
    }

    // Seite neu laden → useTreeData und session werden sauber aus localStorage initialisiert.
    // Das vermeidet alle React-Timing-Probleme mit importData/clearAll.
    window.location.reload();
  }, []);

  // Beim Öffnen/Neuladen: aktuellen Stand vom Server holen
  useEffect(() => {
    let savedSession = null, local = null, fresh = null, unsaved = null;
    try {
      savedSession = JSON.parse(localStorage.getItem('stammbaum_session') || 'null');
      local = JSON.parse(localStorage.getItem('familytree_data') || 'null');
      fresh = sessionStorage.getItem(FRESH_KEY);
      sessionStorage.removeItem(FRESH_KEY);
      unsaved = localStorage.getItem(UNSAVED_KEY);
    } catch {}
    if (!savedSession?.treeCode) return;
    const code = savedSession.treeCode;

    // Soeben vom Server geladen (Dashboard / neuer Stammbaum) → nichts zu tun
    if (fresh === code) { setTreeLoading(false); return; }
    // Nach dem Warten auf eine Sperre immer den Stand vom Server nehmen
    let forceServer = false;
    try { forceServer = sessionStorage.getItem('force_server_load') === code; sessionStorage.removeItem('force_server_load'); } catch {}
    // Lokale Änderungen, die noch nicht auf dem Server sind (z.B. offline) → behalten und hochladen
    if (!forceServer && unsaved === code && local?.persons) {
      isDirtyRef.current = true;
      setTreeLoading(false);
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => handleSaveRef.current && handleSaveRef.current(), 1000);
      return;
    }

    setTreeLoading(true);
    loadTreeWithProgress(code, setLoadProgress)
      .then(serverData => {
        // Server gibt { data: { persons, ... }, treeName } ODER direkt { persons, ... } zurück
        const treeData = (serverData.data && serverData.data.persons) ? serverData.data : serverData;
        if (treeData && Array.isArray(treeData.persons)) {
          // massgebend ist der Status vom Server (z.B. nach Rückgabe durch den Admin)
          const ordered = treeData.status === 'in_bearbeitung' || treeData.status === 'fertiggestellt';
          setIsOrdered(ordered || !!savedSession.readOnly);
          if (JSON.stringify(local) !== JSON.stringify(treeData)) {
            skipDirtyRef.current = true; // Laden ist keine Bearbeitung
            importData(treeData);
          }
        }
        if (serverData.treeName || serverData.role) {
          setSession(s => {
            if (!s) return s;
            const next = { ...s, treeName: serverData.treeName || s.treeName, role: serverData.role || s.role };
            try { localStorage.setItem('stammbaum_session', JSON.stringify(next)); } catch {}
            return next;
          });
        }
        setTreeLoading(false);
      })
      .catch((err) => {
        setTreeLoading(false);
        if (err?.status === 401) {
          // nicht (mehr) eingeloggt → zur Anmeldung
          clearLoginData();
          window.location.reload();
        } else if (err?.status === 403 || err?.status === 404) {
          alert(err.status === 404
            ? 'Dieser Stammbaum existiert nicht mehr.'
            : 'Du hast keinen Zugriff (mehr) auf diesen Stammbaum.');
          localStorage.removeItem('stammbaum_session');
          localStorage.removeItem('familytree_data');
          try { localStorage.removeItem(UNSAVED_KEY); } catch {}
          window.location.reload();
        }
        // sonst (offline): lokale Kopie bleibt sichtbar
      });
  }, []); // eslint-disable-line

  // ── Bearbeitungs-Sperre: immer nur eine Person (bzw. ein Gerät) gleichzeitig ─
  const requestLock = useCallback(async (force = false) => {
    if (!session?.treeCode) return null;
    try {
      const res = await fetch(`${API}/api/user/tree/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ treeCode: session.treeCode, clientId: CLIENT_ID, force }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; } // offline → Zustand beibehalten
  }, [session?.treeCode]); // eslint-disable-line

  const applyLockResult = useCallback((r) => {
    if (!r || r.ordered) return;
    if (r.locked) {
      if (lockedByRef.current) {
        // vorher gesperrt → jetzt frei: aktuellen Stand vom Server laden
        try { sessionStorage.setItem('force_server_load', session.treeCode); } catch {}
        window.location.reload();
        return;
      }
      hasLockRef.current = true;
      // jemand möchte übernehmen → speichern und übergeben
      if (r.takeoverRequested) handoverRef.current && handoverRef.current(r.takeoverRequested.email);
    } else {
      // nur „verloren“, wenn wirklich noch etwas ungespeichert war
      const lost = hasLockRef.current && isDirtyRef.current;
      hasLockRef.current = false;
      setLockedBy(prev => ({
        email: r.lockedBy, sameUser: !!r.sameUser,
        lost: lost || (!!prev?.lost && prev.email === r.lockedBy),
        handedOver: !!prev?.handedOver && prev.email === r.lockedBy,
      }));
      if (r.takeover) setTakeover(t => (t ? { ...t, ...r.takeover } : t));
    }
  }, [session?.treeCode]); // eslint-disable-line

  useEffect(() => {
    if (!session?.treeCode || treeLoading || isOrdered || session.readOnly || !userSession?.token) return;
    let stopped = false;
    const tick = async () => { if (!stopped) applyLockResult(await requestLock(false)); };
    tick();
    const interval = setInterval(tick, LOCK_HEARTBEAT);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    // Beim Schliessen der Seite die Sperre freigeben (sendBeacon kann keine Header → Token im Body)
    const onHide = () => {
      if (!hasLockRef.current) return;
      try {
        const body = JSON.stringify({ treeCode: session.treeCode, clientId: CLIENT_ID, token: authToken() });
        navigator.sendBeacon(`${API}/api/user/tree/unlock`, new Blob([body], { type: 'application/json' }));
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onHide);
    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onHide);
    };
  }, [session?.treeCode, session?.readOnly, treeLoading, isOrdered, userSession?.token, requestLock, applyLockResult]);

  // ── Bearbeitung übernehmen ────────────────────────────────────────────
  // Wer anschaut, kann übernehmen. Der aktuelle Bearbeiter speichert zuerst und übergibt dann.
  const [takeover, setTakeover] = useState(null);       // eigene Anfrage: { saving, canForce, waitedSeconds }
  const [handingOver, setHandingOver] = useState(null); // wir übergeben gerade an diese E-Mail
  const handoverRef = useRef(null);
  const handoverBusyRef = useRef(false);

  const postTakeover = useCallback(async (path, extra = {}) => {
    try {
      const res = await fetch(`${API}/api/user/tree/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ treeCode: session?.treeCode, clientId: CLIENT_ID, ...extra }),
      });
      const d = await res.json().catch(() => ({}));
      return res.ok ? d : { error: d.error || 'Fehler' };
    } catch { return null; }
  }, [session?.treeCode]);

  const reloadFromServer = useCallback(() => {
    try { sessionStorage.setItem('force_server_load', session?.treeCode); } catch {}
    window.location.reload();
  }, [session?.treeCode]);

  const handleTakeoverResult = useCallback((r) => {
    if (!r) return;
    if (r.error) { alert(r.error); setTakeover(null); return; }
    if (r.locked) { reloadFromServer(); return; }
    if (r.takeover) setTakeover(r.takeover);
  }, [reloadFromServer]);

  const startTakeover = useCallback(async () => {
    setTakeover({ saving: false, canForce: false, waitedSeconds: 0 });
    handleTakeoverResult(await postTakeover('takeover'));
  }, [postTakeover, handleTakeoverResult]);

  const forceTakeover = useCallback(async () => {
    const who = lockedByRef.current?.email || 'die andere Person';
    if (!window.confirm(`${who} reagiert nicht. Trotzdem übernehmen?\n\nÄnderungen von ${who}, die noch nicht gespeichert wurden, gehen dabei verloren.`)) return;
    handleTakeoverResult(await postTakeover('takeover', { force: true }));
  }, [postTakeover, handleTakeoverResult]);

  const cancelTakeover = useCallback(async () => {
    setTakeover(null);
    await postTakeover('takeover/cancel');
  }, [postTakeover]);

  // Während der Anfrage alle 2 s nachfragen
  useEffect(() => {
    if (!takeover) return;
    const iv = setInterval(async () => handleTakeoverResult(await postTakeover('takeover')), 2000);
    return () => clearInterval(iv);
  }, [!!takeover, postTakeover, handleTakeoverResult]); // eslint-disable-line

  // Wir bearbeiten, jemand möchte übernehmen: zuerst alles speichern, dann übergeben
  handoverRef.current = async (toEmail) => {
    if (handoverBusyRef.current) return;
    handoverBusyRef.current = true;
    setHandingOver(toEmail);
    try {
      await postTakeover('handover', { step: 'saving' });
      clearTimeout(saveTimerRef.current);
      let saved = false;
      for (let i = 0; i < 300; i++) {           // höchstens ~2 Min. auf laufendes Speichern warten
        const r = handleSaveRef.current ? await handleSaveRef.current() : true;
        if (r === null) { await new Promise(res => setTimeout(res, 400)); continue; }
        saved = r === true;
        break;
      }
      if (!saved) return; // Speichern fehlgeschlagen → nicht übergeben (nichts geht verloren)
      const d = await postTakeover('handover', { step: 'done' });
      if (d?.ok) {
        hasLockRef.current = false;
        setLockedBy({ email: toEmail, sameUser: false, lost: false, handedOver: true });
      }
    } finally {
      setHandingOver(null);
      handoverBusyRef.current = false;
    }
  };


  // ── Einladung annehmen (…/?invite=TOKEN) ──────────────────────────────
  useEffect(() => {
    if (!invite?.token || invite.info || invite.error) return;
    fetch(`${API}/api/invite/info`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: invite.token }),
    })
      .then(async r => { const d = await r.json().catch(() => ({})); return r.ok ? setInvite(i => ({ ...i, info: d })) : setInvite(i => ({ ...i, error: d.error || 'Einladung nicht gefunden.' })); })
      .catch(() => setInvite(i => ({ ...i, error: 'Server nicht erreichbar.' })));
  }, [invite]);

  const acceptInvite = useCallback(async (token) => {
    const res = await fetch(`${API}/api/user/invite/accept`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ token }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Die Einladung konnte nicht angenommen werden.');
    return d.treeCode;
  }, []);

  // Nach dem Login: vorgemerkte Einladung annehmen und den Stammbaum öffnen
  useEffect(() => {
    let pending = null;
    try { pending = sessionStorage.getItem('pending_invite'); } catch {}
    if (!pending || !userSession?.token) return;
    try { sessionStorage.removeItem('pending_invite'); } catch {}
    acceptInvite(pending)
      .then(treeCode => {
        try { sessionStorage.setItem('open_tree', treeCode); } catch {}
        window.location.reload();
      })
      .catch(e => alert(e.message));
  }, [userSession?.token, acceptInvite]);

  const handleInviteAccept = useCallback(async () => {
    const info = invite?.info;
    if (!info) return;
    if (userSession?.email && userSession.email.toLowerCase() === info.email.toLowerCase()) {
      try {
        const treeCode = await acceptInvite(invite.token);
        try { sessionStorage.setItem('open_tree', treeCode); } catch {}
        localStorage.removeItem('stammbaum_session');
        localStorage.removeItem('familytree_data');
        window.location.reload();
      } catch (e) { setInvite(i => ({ ...i, error: e.message })); }
      return;
    }
    // nicht (oder mit anderer Adresse) eingeloggt → zuerst mit der eingeladenen Adresse anmelden
    try {
      sessionStorage.setItem('pending_invite', invite.token);
      sessionStorage.setItem('prefill_login', info.email);
    } catch {}
    clearLoginData();
    window.location.reload();
  }, [invite, userSession, acceptInvite]);

  const handleInviteDecline = useCallback(async () => {
    try {
      await fetch(`${API}/api/invite/decline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: invite.token }),
      });
    } catch {}
    setInvite(null);
  }, [invite]);

  // Nur anschauen: bestellt ODER jemand anderes bearbeitet gerade
  const viewOnly = isOrdered || !!lockedBy || !!handingOver;

  // iPhone: Nach Tastatur/Adressleiste kann die Seite verschoben bleiben → wieder nach oben
  useEffect(() => {
    const reset = () => {
      const a = document.activeElement;
      if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return; // während der Eingabe nicht stören
      if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
    };
    const later = () => setTimeout(reset, 150);
    window.addEventListener('scroll', reset, { passive: true });
    window.addEventListener('orientationchange', later);
    document.addEventListener('focusout', later);
    window.visualViewport?.addEventListener('resize', later);
    return () => {
      window.removeEventListener('scroll', reset);
      window.removeEventListener('orientationchange', later);
      document.removeEventListener('focusout', later);
      window.visualViewport?.removeEventListener('resize', later);
    };
  }, []);
  const isOwner = (session?.role || 'owner') === 'owner';

  // Helfer: beim ersten Öffnen eines Stammbaums die Einführung anbieten
  useEffect(() => {
    if (!session?.treeCode || isOwner || treeLoading || isOrdered) return;
    if (!helperTutSeen().includes(session.treeCode)) setShowTutorialOffer(true);
  }, [session?.treeCode, isOwner, treeLoading, isOrdered]);

  // Nachricht von BandeleArt, wenn der Stammbaum zur Überarbeitung zurückgegeben wurde
  const [returnNotice, setReturnNotice] = useState(null); // { message, at }
  useEffect(() => {
    setReturnNotice(null);
    if (!session?.treeCode || !isOwner || isOrdered || treeLoading || !userSession?.token) return;
    let cancelled = false;
    fetch(`${API}/api/user/tree/notice?treeCode=${encodeURIComponent(session.treeCode)}`, { headers: authHeader() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.message) return;
        let hidden = false;
        try { hidden = sessionStorage.getItem('notice_hidden') === `${session.treeCode}|${d.at}`; } catch {}
        if (!hidden) setReturnNotice(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session?.treeCode, isOwner, isOrdered, treeLoading, userSession?.token]);
  const hideReturnNotice = () => {
    try { sessionStorage.setItem('notice_hidden', `${session?.treeCode}|${returnNotice?.at}`); } catch {}
    setReturnNotice(null);
  };

  const selectedPerson = persons.find(p => p.id === selectedPersonId) || null;
  const editPerson     = persons.find(p => p.id === editPersonId)     || null;

  const handleAddSave = useCallback((formData, relationsData) => {
    const sim = simulateAdd(persons, relationships, formData, relationsData);
    const violation = checkChange(persons, relationships, sim.persons, sim.relationships);
    if (violation) { setShowLimitModal(violation); return; }
    addPerson(formData, relationsData);
    setShowAddModal(false);
    setPresetRelation(null);
  }, [addPerson, persons, relationships]);

  const handleColorChange = useCallback((personId, colorIndex) => {
    const partnerOf = new Map();
    relationships.forEach(r => {
      if (r.type === 'partner' || r.type === 'ex-partner') {
        if (!partnerOf.has(r.from)) partnerOf.set(r.from, []);
        if (!partnerOf.has(r.to))   partnerOf.set(r.to, []);
        partnerOf.get(r.from).push(r.to);
        partnerOf.get(r.to).push(r.from);
      }
    });
    const gen1siblings = getColorLevels(persons, relationships)?.gen1siblings || [];
    const gen1sibSet = new Set(gen1siblings);

    // Falls personId kein Geschwister ist, finde das zugehörige Geschwister
    let targetId = personId;
    if (!gen1sibSet.has(personId)) {
      const sibling = (partnerOf.get(personId) || []).find(pid => gen1sibSet.has(pid));
      if (sibling) targetId = sibling;
    }

    setCustomColor(targetId, colorIndex, gen1siblings);
  }, [persons, relationships, setCustomColor]);

  const handleQuickAdd = useCallback((personId, opt) => {
    setPresetRelation({ personId, opt });
    setShowAddModal(true);
  }, []);

  // Edit: formData + relationsData (Array) weitergeben
  const handleEditSave = useCallback((formData, relationsData) => {
    const sim = simulateEdit(persons, relationships, editPersonId, formData, relationsData);
    const violation = checkChange(persons, relationships, sim.persons, sim.relationships);
    if (violation) { setShowLimitModal(violation); return; }
    updatePerson(editPersonId, formData, relationsData);
    setEditPersonId(null);
  }, [updatePerson, editPersonId, persons, relationships]);

  const handleNameClick = useCallback(() => {
    if (!session?.treeCode) return;
    setNameValue(session.treeName || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [session]);

  const handleNameSave = useCallback(async () => {
    setEditingName(false);
    const newName = nameValue.trim() || session?.treeName || 'Stammbaum';
    if (newName === session?.treeName) return;
    const updatedSession = { ...session, treeName: newName };
    setSession(updatedSession);
    try { localStorage.setItem('stammbaum_session', JSON.stringify(updatedSession)); } catch {}
    try {
      await fetch(`${API}/api/tree/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ treeCode: session.treeCode, name: newName }),
      });
    } catch {}
  }, [nameValue, session]);

  const handleOrderSubmitted = useCallback(() => {
    setIsOrdered(true);
    setShowOrderModal(false);
    // Status in localStorage persistieren
    try {
      const savedData = JSON.parse(localStorage.getItem('familytree_data') || '{}');
      savedData.status = 'in_bearbeitung';
      localStorage.setItem('familytree_data', JSON.stringify(savedData));
    } catch {}
    // Der Server setzt den Status bei der Bestellung selbst → nichts mehr speichern
    isDirtyRef.current = false;
    try { localStorage.removeItem(UNSAVED_KEY); } catch {}
  }, []);

  const handleSelectPerson = useCallback((id) => {
    setSelectedPersonId(prev => prev === id ? null : id);
  }, []);

  // ── Fotos: Bildeditor & Kartenvorschau ────────────────────────────────
  const [photoEditorState, setPhotoEditorState] = useState(null); // { personId, photo, edit }
  const [showCardPreview, setShowCardPreview] = useState(false);
  const photoInputRef = useRef(null);
  const photoTargetRef = useRef(null);

  const openPhotoEditor = useCallback((person) => {
    setPhotoEditorState({ personId: person.id, photo: person.photo, edit: person.photoEdit || null, retouch: person.photoRetouch || null });
  }, []);
  const startAddPhoto = useCallback((person) => {
    photoTargetRef.current = person.id;
    photoInputRef.current?.click();
  }, []);
  const handlePhotoFile = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const personId = photoTargetRef.current;
    if (!file || !personId) return;
    const dataUrl = await shrinkPhoto(file);
    setPhotoEditorState({ personId, photo: dataUrl, edit: null, retouch: null });
  }, []);
  const confirmPhotoEdit = useCallback((edit, retouch) => {
    const st = photoEditorState;
    if (!st) return;
    updatePerson(st.personId, { photo: st.photo, photoEdit: edit, photoRetouch: retouch || null });
    setPhotoEditorState(null);
  }, [photoEditorState, updatePerson]);

  const handleEditPerson = useCallback((id) => {
    setEditPersonId(id);
    setSelectedPersonId(null);
  }, []);

  const handleExport = async () => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Fotos extrahieren und als separate Dateien speichern
    const personsWithoutPhotos = persons.map(p => {
      if (!p.photo || !p.photo.startsWith('data:')) return p;
      return { ...p, photo: `photos/${p.id}.jpg` }; // Referenz statt Base64
    });

    // Fotos in ZIP schreiben
    persons.forEach(p => {
      if (!p.photo || !p.photo.startsWith('data:')) return;
      const base64 = p.photo.split(',')[1];
      const ext = p.photo.startsWith('data:image/png') ? 'png' : 'jpg';
      zip.file(`photos/${p.id}.${ext}`, base64, { base64: true });
      // Update Referenz mit korrekter Endung
      personsWithoutPhotos.find(x => x.id === p.id).photo = `photos/${p.id}.${ext}`;
    });

    // JSON ohne eingebettete Base64-Fotos
    const data = { persons: personsWithoutPhotos, relationships, customColors };
    zip.file('stammbaum.json', JSON.stringify(data, null, 2));

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stammbaum_${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isZip = file.name.endsWith('.zip');

    if (isZip) {
      // ZIP-Import mit Fotos
      import('jszip').then(({ default: JSZip }) => {
        JSZip.loadAsync(file).then(async zip => {
          try {
            const jsonFile = zip.file('stammbaum.json');
            if (!jsonFile) { alert('Keine stammbaum.json in der ZIP-Datei gefunden.'); return; }
            const jsonText = await jsonFile.async('string');
            const d = JSON.parse(jsonText);
            if (!d.persons || !d.relationships) { alert('Ungültiges Dateiformat.'); return; }

            // Fotos wiederherstellen
            for (const person of d.persons) {
              if (person.photo && person.photo.startsWith('photos/')) {
                const photoFile = zip.file(person.photo);
                if (photoFile) {
                  const base64 = await photoFile.async('base64');
                  const ext = person.photo.endsWith('.png') ? 'image/png' : 'image/jpeg';
                  person.photo = `data:${ext};base64,${base64}`;
                } else {
                  person.photo = null;
                }
              }
            }

            if (window.confirm('Bestehende Daten werden überschrieben. Fortfahren?')) {
              importData(d);
            }
          } catch { alert('ZIP-Datei konnte nicht gelesen werden.'); }
        });
      });
    } else {
      // JSON-Import (Rückwärtskompatibilität)
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const d = JSON.parse(ev.target.result);
          if (d.persons && d.relationships) {
            if (window.confirm('Bestehende Daten werden überschrieben. Fortfahren?')) importData(d);
          } else alert('Ungültiges Dateiformat.');
        } catch { alert('Datei konnte nicht gelesen werden.'); }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleDeleteTree = useCallback(async () => {
    if (!window.confirm('Stammbaum komplett löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      const res = await fetch(`${API}/api/tree/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ treeCode: session.treeCode }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.error || 'Der Stammbaum konnte nicht gelöscht werden.');
        return;
      }
    } catch {
      alert('Server nicht erreichbar. Der Stammbaum wurde nicht gelöscht.');
      return;
    }
    try { localStorage.removeItem(UNSAVED_KEY); } catch {}
    clearAll();
    localStorage.removeItem('stammbaum_session');
    setSession(null);
  }, [session, clearAll]);

  const handleOrderClick = useCallback(() => {
    const missingPhoto = persons.some(p => !p.photo);
    if (missingPhoto) setShowPhotoWarning(true);
    else setShowOrderModal(true);
  }, [persons]);

  return (
    <div className="app">
      {treeLoading && <TreeLoadingOverlay progress={loadProgress} />}
      <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoFile} />
      {showCardPreview && (
        <CardPreview
          persons={persons}
          relationships={relationships}
          customColors={customColors}
          canEdit={!viewOnly}
          onEditPhoto={openPhotoEditor}
          onAddPhoto={startAddPhoto}
          onClose={() => setShowCardPreview(false)}
        />
      )}
      {photoEditorState && (
        <PhotoEditor
          photo={photoEditorState.photo}
          edit={photoEditorState.edit}
          retouch={photoEditorState.retouch}
          title={photoEditorState.edit ? 'Bild bearbeiten' : 'Foto zuschneiden'}
          onCancel={() => setPhotoEditorState(null)}
          onConfirm={confirmPhotoEdit}
        />
      )}
      {leaving && <TreeLoadingOverlay progress={leaveProgress} label="Stammbaum wird gespeichert…" />}
      {invite && (
        <InviteScreen
          invite={invite}
          currentEmail={userSession?.email || null}
          onAccept={handleInviteAccept}
          onDecline={handleInviteDecline}
          onClose={() => setInvite(null)}
        />
      )}
      {session?.treeCode && showTutorialOffer && !treeLoading && !showTutorial && (
        <TutorialOffer helper={!isOwner} treeName={session?.treeName} onStart={() => closeTutorialOffer(true)} onSkip={() => closeTutorialOffer(false)} />
      )}
      {session?.treeCode && showTutorial && (
        <Tutorial loggedIn={!!userSession} helper={!isOwner} hasPersons={persons.length > 0} onClose={() => setShowTutorial(false)} />
      )}
      {otherTabNotice && (
        <div className="tree-loading-overlay" style={{ zIndex: 3000 }}>
          <div className="tree-loading-card" style={{ background: '#fff', padding: '28px 24px', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Anderer Stammbaum geöffnet</div>
            <p style={{ fontSize: 14, lineHeight: 1.5, color: '#555', marginBottom: 18 }}>
              In einem anderen Tab wurde ein anderer Stammbaum geöffnet. Deine Änderungen in diesem Tab
              wurden gespeichert. Damit nichts durcheinandergerät, kann hier nicht weitergearbeitet werden.
            </p>
            <button className="btn btn--primary" style={{ width: '100%', padding: '11px 0' }} onClick={() => window.location.reload()}>
              Tab neu laden
            </button>
          </div>
        </div>
      )}
      <header className="app-header">
        {/* ── Links: Wechseln-Button + Name + Personen-Anzahl ── */}
        <div className="header-brand">
          {session && (
            <button
              className="hdr-btn hdr-btn--ghost"
              data-tour="back"
              style={{ marginRight: 4 }}
              onClick={() => {
                if (userSession) {
                  // logged-in user → back to dashboard
                  handleBackToDashboard();
                } else if (window.confirm('Zum Startbildschirm zurückkehren? Ungespeicherte Änderungen gehen verloren.')) {
                  localStorage.removeItem('stammbaum_session');
                  setSession(null);
                  clearAll();
                }
              }}
            >
              ←<span className="hdr-long">&nbsp;{userSession ? 'Meine Stammbäume' : 'Wechseln'}</span>
            </button>
          )}
          {editingName ? (
            <input
              ref={nameInputRef}
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={e => {
                if (e.key === 'Enter') nameInputRef.current?.blur();
                if (e.key === 'Escape') setEditingName(false);
              }}
              style={{
                fontFamily: "'Muli', sans-serif",
                fontSize: 17, fontWeight: 700, border: 'none',
                borderBottom: '2px solid #1540dc', background: 'transparent',
                outline: 'none', width: 200, color: '#1a1a1a',
              }}
            />
          ) : (
            <div className="header-name-block">
              <span
                data-tour="title"
                className={`header-title${session?.treeCode ? ' header-title--editable' : ''}`}
                onClick={session?.treeCode ? handleNameClick : undefined}
                title={session?.treeCode ? 'Klicken zum Umbenennen' : ''}
              >
                {session?.treeName || 'Stammbaum-Memo'}
              </span>
              <span className="header-subtitle">
                {persons.length} {persons.length === 1 ? 'Person' : 'Personen'}
                {isOrdered && (
                  <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 10, background: '#fef3e2', color: '#b45309', fontWeight: 700, border: '1px solid #f59e0b', fontSize: 11 }}>
                    In Bearbeitung
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* ── Rechts: Aktionen ── */}
        <div className="header-actions">
          {/* Speicher-Status */}
          {session?.treeCode && saveStatus && (
            <span
              className={`header-save-label header-save-label--${saveStatus === 'toolarge' ? 'error' : saveStatus}`}
              title={saveStatus === 'toolarge' ? 'Der Stammbaum ist zu gross zum Speichern (vermutlich wegen sehr grosser Fotos).' : undefined}
            >
              {saveStatus === 'saving' ? '↑ Speichern…'
                : saveStatus === 'saved' ? '✓ Gespeichert'
                : saveStatus === 'toolarge' ? '✗ Zu gross – nicht gespeichert'
                : '✗ Nicht gespeichert'}
            </span>
          )}

          {/* Speichern */}
          {session?.treeCode && !viewOnly && (
            <button className="hdr-btn hdr-btn--ghost hdr-secondary" data-tour="save" onClick={handleSave} disabled={saveStatus === 'saving'}>
              Speichern
            </button>
          )}

          {/* Preis */}
          {persons.length > 0 && !viewOnly && (
            <button className="hdr-btn hdr-btn--ghost hdr-secondary" data-tour="price" onClick={() => setShowPriceModal(true)}>
              Preis
            </button>
          )}

          {/* Löschen */}
          {session?.treeCode && persons.length > 0 && !viewOnly && isOwner && (
            <button className="hdr-btn hdr-btn--icon hdr-secondary" data-tour="delete" onClick={handleDeleteTree} title="Stammbaum löschen">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14H6L5 6"/>
                <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
              </svg>
            </button>
          )}

          {/* Tutorial / Hilfe */}
          {session?.treeCode && !viewOnly && (
            <button className="hdr-btn hdr-btn--icon hdr-help hdr-secondary" data-tour="help" onClick={() => setShowTutorial(true)} title="Einführung anzeigen" aria-label="Einführung anzeigen">
              ?
            </button>
          )}

          {/* Kartenvorschau */}
          {session?.treeCode && persons.length > 0 && (
            <button className={`hdr-btn hdr-btn--ghost${viewOnly ? '' : ' hdr-secondary'}`} data-tour="preview" onClick={() => setShowCardPreview(true)}>
              Vorschau
            </button>
          )}

          {/* + Person */}
          {!viewOnly && (
            <button className="hdr-btn hdr-btn--ghost" data-tour="add-person" onClick={() => setShowAddModal(true)}>
              + Person
            </button>
          )}

          {/* Stammbaum bestellen */}
          {session?.treeCode && persons.length > 0 && !viewOnly && isOwner && (
            <button className="hdr-btn hdr-btn--order" data-tour="order" onClick={handleOrderClick}>
              <span className="hdr-long">Stammbaum bestellen</span>
              <span className="hdr-short">Bestellen</span>
            </button>
          )}

          {/* „⋯“-Menü: auf schmalen Bildschirmen landen Speichern / Preis / Löschen hier */}
          {session?.treeCode && !viewOnly && (
            <div className="hdr-more">
              <button className="hdr-btn hdr-btn--ghost" data-tour="more" onClick={() => setShowMoreMenu(m => !m)} title="Weitere Aktionen" aria-label="Weitere Aktionen">
                ⋯
              </button>
              {showMoreMenu && (
                <>
                  <div className="hdr-more-backdrop" onClick={() => setShowMoreMenu(false)} />
                  <div className="hdr-more-menu">
                    {session?.treeCode && !viewOnly && (
                      <button onClick={() => { setShowMoreMenu(false); handleSave(); }} disabled={saveStatus === 'saving'}>
                        Speichern
                      </button>
                    )}
                    {persons.length > 0 && !viewOnly && (
                      <button onClick={() => { setShowMoreMenu(false); setShowPriceModal(true); }}>Preis berechnen</button>
                    )}
                    {session?.treeCode && !viewOnly && (
                      <button onClick={() => { setShowMoreMenu(false); setShowTutorial(true); }}>Einführung anzeigen</button>
                    )}
                    {session?.treeCode && persons.length > 0 && (
                      <button onClick={() => { setShowMoreMenu(false); setShowCardPreview(true); }}>Vorschau der Karten</button>
                    )}
                    {session?.treeCode && persons.length > 0 && !viewOnly && isOwner && (
                      <button className="hdr-more-danger" onClick={() => { setShowMoreMenu(false); handleDeleteTree(); }}>
                        Stammbaum löschen
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {session?.treeCode && returnNotice && (
        <div className="lock-banner notice-banner">
          <div>
            <strong>Nachricht von BandeleArt:</strong> Dein Stammbaum wurde zur Überarbeitung zurückgegeben.
            <div className="notice-text">{returnNotice.message}</div>
            <div className="notice-sub">Wenn alles angepasst ist, kannst du ihn wie gewohnt erneut bestellen.</div>
          </div>
          <button className="notice-close" onClick={hideReturnNotice} aria-label="Hinweis ausblenden" title="Ausblenden">×</button>
        </div>
      )}
      {session?.treeCode && handingOver && (
        <div className="lock-banner">
          <span><strong>{handingOver}</strong> möchte den Stammbaum bearbeiten. Deine Änderungen werden gespeichert und dann übergeben …</span>
        </div>
      )}
      {session?.treeCode && lockedBy && !handingOver && (
        <div className="lock-banner">
          <span>
            {takeover
              ? (takeover.canForce
                  ? <><strong>{lockedBy.email}</strong> reagiert nicht (vielleicht ist das Gerät im Ruhezustand). Du kannst trotzdem übernehmen.</>
                  : takeover.saving
                    ? <><strong>{lockedBy.email}</strong> speichert gerade. Danach kannst du hier weiterarbeiten …</>
                    : <>Anfrage an <strong>{lockedBy.email}</strong> gesendet. Die Änderungen werden zuerst dort gespeichert – einen Moment bitte …</>)
              : lockedBy.sameUser
                ? <>Du bearbeitest diesen Stammbaum gerade auf einem anderen Gerät oder in einem anderen Tab. Hier kannst du ihn nur anschauen.</>
                : lockedBy.handedOver
                  ? <><strong>{lockedBy.email}</strong> hat die Bearbeitung übernommen. Deine Änderungen wurden vorher gespeichert. Du kannst jetzt nur anschauen.</>
                  : <><strong>{lockedBy.email}</strong> bearbeitet diesen Stammbaum gerade. Du kannst ihn anschauen oder die Bearbeitung übernehmen.</>}
            {lockedBy.lost && !takeover && <> Deine letzten, noch nicht gespeicherten Änderungen konnten nicht übernommen werden.</>}
          </span>
          <div className="lock-banner-actions">
            {!takeover && lockedBy.sameUser && (
              <button className="hdr-btn hdr-btn--ghost" onClick={startTakeover}>Hier bearbeiten</button>
            )}
            {!takeover && !lockedBy.sameUser && (
              <button className="hdr-btn hdr-btn--ghost" onClick={startTakeover}>Bearbeitung übernehmen</button>
            )}
            {takeover && !takeover.canForce && <span className="lock-spinner" aria-hidden />}
            {takeover?.canForce && (
              <button className="hdr-btn hdr-btn--ghost" onClick={forceTakeover}>Trotzdem übernehmen</button>
            )}
            {takeover && (
              <button className="hdr-btn hdr-btn--ghost" onClick={cancelTakeover}>Abbrechen</button>
            )}
          </div>
        </div>
      )}

      <main className="app-main" onClick={() => setSelectedPersonId(null)}>
        <TreeCanvas
          persons={persons}
          relationships={relationships}
          selectedId={selectedPersonId}
          onSelectPerson={handleSelectPerson}
          onEditPerson={viewOnly ? null : handleEditPerson}
          onQuickAdd={viewOnly ? null : handleQuickAdd}
          customColors={customColors}
          onColorChange={viewOnly ? null : handleColorChange}
        />
        {selectedPerson && (
          <PersonDetailPanel
            person={selectedPerson}
            persons={persons}
            relationships={relationships}
            onEdit={viewOnly ? null : handleEditPerson}
            onDelete={viewOnly ? null : deletePerson}
            onEditPhoto={viewOnly ? null : openPhotoEditor}
            onAddPhoto={viewOnly ? null : startAddPhoto}
            onClose={() => setSelectedPersonId(null)}
          />
        )}
      </main>

      {!viewOnly && showAddModal && (
        <AddPersonModal
          persons={persons}
          relationships={relationships}
          onClose={() => { setShowAddModal(false); setPresetRelation(null); }}
          onSave={handleAddSave}
          presetRelation={presetRelation}
        />
      )}

      {!viewOnly && editPersonId && editPerson && (
        <AddPersonModal
          persons={persons}
          relationships={relationships}
          editPerson={editPerson}
          onClose={() => setEditPersonId(null)}
          onSave={handleEditSave}
        />
      )}
      {showLimitModal && <LimitModal reason={showLimitModal} onClose={() => setShowLimitModal(null)} />}
      {showPriceModal && <PriceModal persons={persons.length} retouchCount={persons.filter(p => p.photo && p.photoRetouch).length} onClose={() => setShowPriceModal(false)} />}
      {showOrderModal && session?.treeCode && (
        <OrderModal
          personsCount={persons.length}
          retouchCount={persons.filter(p => p.photo && p.photoRetouch).length}
          session={session}
          onClose={() => setShowOrderModal(false)}
          onOrderSubmitted={handleOrderSubmitted}
        />
      )}
      {/* Dashboard für eingeloggte Benutzer (wenn kein Baum geöffnet) */}
      {!session && userSession && !showNewTreeFromDashboard && (
        <UserDashboard
          email={userSession.email}
          token={userSession.token}
          onSelectTree={handleSelectTree}
          onLogout={handleUserLogout}
          onCreateNew={() => setShowNewTreeFromDashboard(true)}
        />
      )}

      {/* Welcome-Screen: für nicht eingeloggte Benutzer ODER "Neuer Stammbaum" vom Dashboard */}
      {!session && (!userSession || showNewTreeFromDashboard) && (
        <WelcomeScreen
          onStart={handleStart}
          onLogin={!userSession ? handleLogin : undefined}
          magicLogin={!userSession ? MAGIC_LOGIN : undefined}
          prefillEmail={!userSession ? PREFILL_LOGIN : undefined}
          onCancel={showNewTreeFromDashboard ? () => setShowNewTreeFromDashboard(false) : undefined}
          initialStep={showNewTreeFromDashboard ? 'new_email' : undefined}
          userEmail={showNewTreeFromDashboard ? userSession?.email : undefined}
          userToken={showNewTreeFromDashboard ? userSession?.token : undefined}
        />
      )}

      {/* Foto-Warnung */}
      {showPhotoWarning && (() => {
        const missing = persons.filter(p => !p.photo).length;
        return (
          <div className="modal-overlay" onClick={() => setShowPhotoWarning(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: '32px 28px', textAlign: 'center' }}>
              <div style={{ marginBottom: 12 }}></div>
              <h2 style={{ fontSize: 20, marginBottom: 12 }}>Nicht alle Personen haben ein Foto</h2>
              <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                <strong>{missing} {missing === 1 ? 'Person hat' : 'Personen haben'}</strong> noch kein Foto. {missing === 1 ? 'Diese Person wird' : 'Diese Personen werden'} bei der Fotokarte im Stammbaum-Memo kein Foto haben.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn--ghost" onClick={() => setShowPhotoWarning(false)} style={{ flex: 1, padding: '12px 0' }}>
                  Abbrechen
                </button>
                <button className="btn btn--primary" onClick={() => { setShowPhotoWarning(false); setShowOrderModal(true); }} style={{ flex: 1, padding: '12px 0' }}>
                  Trotzdem bestellen
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
